// S-3050A: Cloudflare-aware Videos page with Realtime updates
// MVPFIGMA: Redesigned to match MVP design system
// PLA-143: V3 Upload Nav Unification - Top Nav uses V3 pipeline
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Play, AlertCircle, CheckCircle2, Loader2, RefreshCw, Trash2, Video as VideoIcon, Upload } from 'lucide-react';
import { GolfCard, GolfCardContent, GolfCardTitle } from '@/components/golf/golf-card';
import { GolfButton } from '@/components/golf/golf-button';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { useSwingRunsFeed } from '@/hooks/useSwingRunsFeed';
import type { SwingRun } from '@/types/swing-run';
import { getDisplayFilename, getDurationSec, formatDuration } from '@/types/swing-run';
import { FEATURE_FLAGS } from '@/lib/feature-flags';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { UploadButton } from '@/components/videos/UploadButton';
import { BottomTabs } from "@/mvp/navigation/BottomTabs";
import { useActiveV3Pipeline } from '@/hooks/useActiveV3Pipeline';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const statusConfig = {
  uploaded: { 
    icon: Clock, 
    label: 'Uploaded', 
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
  },
  processing: { 
    icon: Loader2, 
    label: 'Processing', 
    color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
    animate: true
  },
  ready: { 
    icon: CheckCircle2, 
    label: 'Ready', 
    color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
  },
  failed: { 
    icon: AlertCircle, 
    label: 'Failed', 
    color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
  },
};

interface VideoCardProps {
  run: SwingRun;
  onDelete: (runId: string) => void;
  deleting: boolean;
}

function VideoCard({ run, onDelete, deleting }: VideoCardProps) {
  const navigate = useNavigate();
  const config = statusConfig[run.status as keyof typeof statusConfig] || statusConfig.processing;
  const StatusIcon = config.icon;
  const displayFilename = getDisplayFilename(run);
  const durationSec = getDurationSec(run);
  const formattedDuration = formatDuration(durationSec);

  // Cloudflare thumbnail URL (public, no signing required for thumbnails)
  const thumbnailUrl = run.cf_playback_url 
    ? run.cf_playback_url.replace('/manifest/video.m3u8', '/thumbnails/thumbnail.jpg')
    : null;

  const handlePlay = () => {
    if (run.status === 'ready') {
      navigate(`/analysis/${run.id}`);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Delete "${displayFilename}"? This cannot be undone.`)) {
      onDelete(run.id);
    }
  };

  return (
    <div 
      className={cn(
        "bg-white rounded-2xl overflow-hidden shadow-[0_2px_8px_rgba(13,56,42,0.08)] transition-all duration-200 hover:shadow-[0_4px_16px_rgba(13,56,42,0.12)] cursor-pointer",
        run.status === 'ready' && "hover:border-2 hover:border-[#196644]"
      )}
      onClick={handlePlay}
    >
      <div className="relative aspect-video bg-muted/20 overflow-hidden rounded-t-lg">
        {/* Cloudflare thumbnail or placeholder */}
        {thumbnailUrl ? (
          <img 
            src={thumbnailUrl}
            alt={displayFilename}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to placeholder if thumbnail fails to load
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : null}
        
        {/* Placeholder (shows when no thumbnail or thumbnail fails) */}
        <div className={cn(
          "w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900",
          thumbnailUrl && "hidden"
        )}>
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Play size={28} className="text-primary ml-1" />
          </div>
        </div>
        
        {/* Status badge */}
        <div className="absolute top-2 right-2">
          <Badge className={config.color}>
            <StatusIcon 
              size={12} 
              className={cn("mr-1", config.animate && "animate-spin")} 
            />
            {config.label}
          </Badge>
        </div>

        {/* Duration badge */}
        {formattedDuration && (
          <div className="absolute bottom-2 right-2">
            <Badge variant="secondary" className="bg-black/60 text-white border-0">
              {formattedDuration}
            </Badge>
          </div>
        )}

        {/* Cloudflare badge */}
        {run.cf_stream_uid && (
          <div className="absolute bottom-2 left-2">
            <Badge variant="secondary" className="bg-blue-600/90 text-white border-0 text-xs">
              Cloudflare
            </Badge>
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#0D382A] truncate" title={displayFilename}>
              {displayFilename}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {new Date(run.created_at).toLocaleDateString()} • {new Date(run.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          
          <div className="flex gap-1">
            {run.status === 'ready' && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={handlePlay}
                className="border-[#196644] text-[#196644] hover:bg-[#196644] hover:text-white"
              >
                <Play size={14} className="mr-1" />
                Play
              </Button>
            )}
            
            <Button 
              size="sm" 
              variant="ghost"
              onClick={handleDelete}
              disabled={deleting}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              {deleting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Videos() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  // Use Cloudflare-aware hook with Realtime + polling
  const { 
    runs, 
    isLoading, 
    error, 
    refresh 
  } = useSwingRunsFeed({
    pollingInterval: 20000, // 20s fallback
    enableRealtime: true,
    limit: 50,
  });

  // PLA-143: Track active V3 pipeline for upload button state
  const { 
    uploadDisabled, 
    disabledCopy,
    hasActivePipeline 
  } = useActiveV3Pipeline();

  // PLA-143: V3 Upload entry point - navigates to unified V3 upload flow
  const handleV3Upload = () => {
    if (uploadDisabled) {
      toast({
        title: "Upload in progress",
        description: disabledCopy,
      });
      return;
    }
    // Navigate to V3 upload flow (same as Bottom Nav)
    navigate('/mvp/upload');
  };

  const handleDelete = async (runId: string) => {
    setDeletingIds(prev => new Set(prev).add(runId));
    
    try {
      const { error: deleteError } = await supabase
        .from('swing_runs')
        .delete()
        .eq('id', runId);

      if (deleteError) throw deleteError;

      toast({
        title: "Deleted",
        description: "Video deleted successfully.",
      });

      // Refresh list
      await refresh();
    } catch (err) {
      console.error('Delete failed:', err);
      toast({
        title: "Delete Failed",
        description: err instanceof Error ? err.message : "Failed to delete video.",
        variant: "destructive",
      });
    } finally {
      setDeletingIds(prev => {
        const next = new Set(prev);
        next.delete(runId);
        return next;
      });
    }
  };

  if (!user) {
    navigate('/auth');
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FAFAFA] to-[#F5F5F5] pb-20">
        <header className="bg-gradient-to-r from-[#0D382A] to-[#196644] px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between h-20 shadow-sm">
          <div className="w-10" />
          <div className="flex-1 flex justify-center">
            <img 
              src="/nodoubles_logo_trans.png" 
              alt="No Doubles Golf Co" 
              className="h-12 object-contain"
            />
          </div>
          <div className="w-10" />
        </header>
        <main className="max-w-4xl mx-auto px-6 pt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-[0_2px_8px_rgba(13,56,42,0.08)] animate-pulse">
                <div className="aspect-video bg-gray-200" />
                <div className="p-4">
                  <div className="h-4 bg-gray-200 rounded mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        </main>
        <BottomTabs />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FAFAFA] to-[#F5F5F5] pb-20">
        <header className="bg-gradient-to-r from-[#0D382A] to-[#196644] px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between h-20 shadow-sm">
          <div className="w-10" />
          <div className="flex-1 flex justify-center">
            <img 
              src="/nodoubles_logo_trans.png" 
              alt="No Doubles Golf Co" 
              className="h-12 object-contain"
            />
          </div>
          <div className="w-10" />
        </header>
        <main className="max-w-4xl mx-auto px-6 pt-8">
          <div className="bg-white rounded-2xl p-8 shadow-[0_2px_8px_rgba(13,56,42,0.08)] text-center">
            <AlertCircle size={48} className="mx-auto text-orange-500 mb-4" />
            <h3 className="text-xl font-semibold text-[#0D382A] mb-2">Failed to Load Videos</h3>
            <p className="text-gray-600 mb-4">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
            <Button
              onClick={refresh}
              className="bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] text-[#0D382A] hover:shadow-lg"
            >
              Try Again
            </Button>
          </div>
        </main>
        <BottomTabs />
      </div>
    );
  }

  // Filter to only show Cloudflare videos (cf_stream_uid present)
  // Legacy videos (legacy_video_id) are excluded unless SHOW_LEGACY_VIDEOS is true
  const cloudflareRuns = runs.filter(run => run.cf_stream_uid);
  const displayRuns = FEATURE_FLAGS.SHOW_LEGACY_VIDEOS 
    ? runs 
    : cloudflareRuns;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAFA] to-[#F5F5F5] pb-20">
      {/* MVPFIGMA Header - PLA-143: V3 Upload Nav Unification */}
      <header className="bg-gradient-to-r from-[#0D382A] to-[#196644] px-6 py-4 flex items-center justify-between h-20 shadow-sm">
        <div className="w-10" />
        <div className="flex-1 flex justify-center">
          <img 
            src="/nodoubles_logo_trans.png" 
            alt="No Doubles Golf Co" 
            className="h-12 object-contain"
          />
        </div>
        <div className="flex items-center gap-2">
          {FEATURE_FLAGS.FEATURE_VIDEOS_UPLOAD && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  onClick={handleV3Upload}
                  disabled={uploadDisabled}
                  className={cn(
                    "text-white/90 hover:text-white hover:bg-white/10",
                    uploadDisabled && "opacity-50 cursor-not-allowed"
                  )}
                  aria-label={uploadDisabled ? disabledCopy : "Upload video"}
                >
                  {hasActivePipeline ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Upload className="w-5 h-5" />
                  )}
                </Button>
              </TooltipTrigger>
              {uploadDisabled && (
                <TooltipContent side="bottom" className="max-w-xs">
                  <p>{disabledCopy}</p>
                </TooltipContent>
              )}
            </Tooltip>
          )}
          <Button
            variant="ghost"
            onClick={refresh}
            className="text-white/90 hover:text-white hover:bg-white/10"
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Stats Section */}
        <div className="pt-8 pb-6">
          <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)]">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Total</p>
                <p className="text-2xl font-semibold text-[#0D382A]">{displayRuns.length}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Processing</p>
                <p className="text-2xl font-semibold text-yellow-600">
                  {displayRuns.filter(r => r.status === 'processing' || r.status === 'uploaded').length}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Ready</p>
                <p className="text-2xl font-semibold text-green-600">
                  {displayRuns.filter(r => r.status === 'ready').length}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Failed</p>
                <p className="text-2xl font-semibold text-red-600">
                  {displayRuns.filter(r => r.status === 'failed').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Videos Grid */}
        <div className="pb-6">
          {displayRuns.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 shadow-[0_2px_8px_rgba(13,56,42,0.08)] text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] flex items-center justify-center mx-auto mb-4">
                <VideoIcon className="w-8 h-8 text-[#0D382A]" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D382A] mb-2">No Videos Yet</h3>
              <p className="text-gray-600 mb-6">
                {!FEATURE_FLAGS.SHOW_LEGACY_VIDEOS && runs.length > 0 
                  ? `You have ${runs.length} legacy video(s), but Cloudflare videos are not yet uploaded.`
                  : "Start by uploading your first video to get started."
                }
              </p>
              {/* PLA-143: V3 Upload entry point */}
              <Button
                onClick={handleV3Upload}
                disabled={uploadDisabled}
                className={cn(
                  "bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] text-[#0D382A] hover:shadow-lg",
                  uploadDisabled && "opacity-50 cursor-not-allowed"
                )}
              >
                {hasActivePipeline ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-5 h-5 mr-2" />
                )}
                {uploadDisabled ? "Processing..." : "Upload Video"}
              </Button>
              {uploadDisabled && (
                <p className="text-sm text-gray-500 mt-2">{disabledCopy}</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayRuns.map((run) => (
                <VideoCard 
                  key={run.id} 
                  run={run}
                  onDelete={handleDelete}
                  deleting={deletingIds.has(run.id)}
                />
              ))}
            </div>
          )}
        </div>

        {displayRuns.length === 50 && (
          <div className="pb-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4">
              <p className="text-sm text-yellow-800 text-center">
                You've reached the current library limit (50 videos).
              </p>
            </div>
          </div>
        )}
      </main>
      <BottomTabs />
    </div>
  );
}
